import React, { useState, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { ArrowLeft, Plus, Calendar, Lightbulb, Heart, Archive, RotateCcw, Plane } from 'lucide-react';
import ActivityBrowseGrid from './ActivityBrowseGrid';
import ActivityDetailPanel from './ActivityDetailPanel';
import EventForm from './EventForm';
import ScheduleActivityModal from './ScheduleActivityModal';
import MobileActivityFilters from './MobileActivityFilters';
import FamilyInvitations from './FamilyInvitations';
import TripBrowseGrid from './Trips/TripBrowseGrid';
import TripForm from './Trips/TripForm';
import TripDetailPanel from './Trips/TripDetailPanel';
import './activity-browse-page.css';

export default function ActivityBrowsePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { familyActivities = [], addFamilyActivity, scheduleToCalendar, currentUser, unarchiveFamilyActivity } = useFamilyContext();
  
  // Check which tab to show
  const currentTab = searchParams.get('tab') || 'activities';
  
  // Local state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showTripForm, setShowTripForm] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showTripDetail, setShowTripDetail] = useState(false);
  const [tripDetailOrigin, setTripDetailOrigin] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showActivityDetail, setShowActivityDetail] = useState(false);
  const [activityDetailOrigin, setActivityDetailOrigin] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [activityToSchedule, setActivityToSchedule] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    categories: ['all'],
    status: 'all',
    rating: 'all',
    showCompleted: false,
    showFavorites: false
  });
  const [showArchived, setShowArchived] = useState(false);

  // Process data for badge counts
  const processedData = React.useMemo(() => {
    if (!familyActivities || !currentUser) {
      return {
        activeVotes: [],
        pendingRSVPs: []
      };
    }

    const activeVotes = familyActivities.filter(activity => {
      if (activity.confirmed_for_calendar) return false;
      const ratings = activity.ratings || {};
      const hasRatings = Object.keys(ratings).length > 0;
      const currentUserHasVoted = ratings[currentUser.id] !== undefined;
      return hasRatings || !currentUserHasVoted;
    });

    const pendingRSVPs = familyActivities.filter(activity => {
      if (!activity.confirmed_for_calendar) return false;
      if (activity.start_date) {
        const eventDate = new Date(activity.start_date);
        const now = new Date();
        if (eventDate < now) return false;
      }
      const rsvps = activity.rsvps || {};
      return rsvps[currentUser.id] === undefined;
    });

    return {
      activeVotes,
      pendingRSVPs
    };
  }, [familyActivities, currentUser]);

  // Separate trips from regular activities
  const trips = useMemo(() => {
    return familyActivities.filter(activity => activity.activity_type === 'trip');
  }, [familyActivities]);
  
  // Process and filter activities for browsing (exclude trips)
  const browseActivities = useMemo(() => {
    // Filter based on archive view toggle and exclude trips
    let filtered = familyActivities.filter(activity => {
      // Exclude trips from regular activities view
      if (activity.activity_type === 'trip') return false;
      
      if (showArchived) {
        // Show only archived activities
        return activity.status === 'archived';
      } else {
        // Show only active (non-archived, non-confirmed) activities
        return !activity.confirmed_for_calendar && activity.status !== 'archived';
      }
    });
    
    // Filter for completed activities
    if (filters.showCompleted) {
      filtered = filtered.filter(activity => activity.completed_date);
    } else if (filters.showCompleted === false && filters.status !== 'completed') {
      // Don't filter out completed unless explicitly set to hide
    }

    // Apply category filter with improved matching
    if (!filters.categories.includes('all')) {
      filtered = filtered.filter(activity => {
        const activityCategory = activity.category?.toLowerCase() || 'other';
        return filters.categories.some(filterCat => {
          // Direct match
          if (filterCat === activityCategory) return true;
          
          // Enhanced category mappings for better accuracy
          const categoryMappings = {
            'food': [
              'food', 'dining', 'food & dining', 'dining out', 'casual bites',
              'restaurant', 'restaurants', 'cafe', 'cafes', 'bakery', 'dessert',
              'brunch', 'lunch', 'dinner', 'breakfast', 'snacks', 'treats',
              'food truck', 'ice cream', 'pizza', 'sushi', 'mexican', 'italian',
              'chinese', 'thai', 'indian', 'american', 'bbq', 'seafood'
            ],
            'entertainment': [
              'entertainment', 'movies', 'shows', 'theater', 'theatre',
              'concert', 'concerts', 'music', 'comedy', 'performance',
              'circus', 'magic show', 'live show', 'festival', 'fair'
            ],
            'outdoor': [
              'outdoor', 'nature', 'park', 'parks', 'hiking', 'camping',
              'beach', 'lake', 'mountain', 'trail', 'picnic', 'garden',
              'zoo', 'aquarium', 'outdoors', 'outside', 'wilderness'
            ],
            'sports': [
              'sports', 'fitness', 'sports & fitness', 'gym', 'exercise',
              'basketball', 'soccer', 'football', 'baseball', 'tennis',
              'swimming', 'golf', 'bowling', 'skating', 'skiing', 'climbing',
              'yoga', 'dance', 'martial arts', 'running', 'cycling', 'bike'
            ],
            'cultural': [
              'arts', 'culture', 'arts & culture', 'cultural', 'museum',
              'gallery', 'art gallery', 'exhibition', 'history', 'historical',
              'heritage', 'library', 'landmark', 'architecture', 'tour'
            ],
            'educational': [
              'educational', 'learning', 'education', 'science', 'stem',
              'workshop', 'class', 'lesson', 'tutorial', 'study', 'research',
              'discovery', 'exploration', 'knowledge', 'skill', 'training'
            ],
            'social': [
              'social', 'party', 'gathering', 'meetup', 'playdate',
              'celebration', 'birthday', 'reunion', 'get-together', 'hangout'
            ],
            'creative': [
              'creative', 'art', 'craft', 'crafts', 'diy', 'painting',
              'drawing', 'pottery', 'sculpture', 'photography', 'music',
              'writing', 'design', 'maker', 'build', 'create'
            ],
            'adventure': [
              'adventure', 'explore', 'exploration', 'expedition', 'journey',
              'quest', 'discovery', 'challenge', 'thrill', 'excitement'
            ],
            'relaxation': [
              'relaxation', 'wellness', 'spa', 'meditation', 'mindfulness',
              'calm', 'peaceful', 'quiet', 'rest', 'recharge', 'self-care'
            ],
            'shopping': [
              'shopping', 'shop', 'store', 'stores', 'mall', 'market',
              'boutique', 'retail', 'browse', 'purchase', 'buy'
            ],
            'other': ['other', 'miscellaneous', 'misc', 'various', 'general']
          };
          
          const mappedCategories = categoryMappings[filterCat] || [filterCat];
          return mappedCategories.some(mapped => {
            // Check if activity category contains or is contained by mapped term
            const normalizedMapped = mapped.toLowerCase();
            return activityCategory.includes(normalizedMapped) || 
                   normalizedMapped.includes(activityCategory) ||
                   activityCategory.split(/[\s&,]+/).some(word => 
                     normalizedMapped.split(/[\s&,]+/).includes(word)
                   );
          });
        });
      });
    }

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(activity => {
        const ratings = Object.values(activity.ratings || {});
        const hasRatings = ratings.length > 0;
        const averageRating = hasRatings ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length : 0;
        
        switch (filters.status) {
          case 'unrated':
            return !hasRatings;
          case 'rated':
            return hasRatings;
          case 'high-rated':
            return hasRatings && averageRating >= 7;
          case 'needs-votes': {
            // Activities where current user hasn't voted yet (regardless of other votes)
            const currentUserHasVoted = activity.ratings && activity.ratings[currentUser?.id] !== undefined;
            return !currentUserHasVoted;
          }
          case 'completed':
            return activity.completed_date != null;
          case 'not-completed':
            return !activity.completed_date;
          case 'favorites':
            return activity.is_favorite === true;
          default:
            return true;
        }
      });
    }
    
    // Apply favorite filter
    if (filters.showFavorites) {
      filtered = filtered.filter(activity => activity.is_favorite === true);
    }

    // Apply rating filter
    if (filters.rating !== 'all') {
      filtered = filtered.filter(activity => {
        const ratings = Object.values(activity.ratings || {});
        const hasRatings = ratings.length > 0;
        const averageRating = hasRatings ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length : 0;
        
        switch (filters.rating) {
          case '8+':
            return hasRatings && averageRating >= 8;
          case '6-8':
            return hasRatings && averageRating >= 6 && averageRating < 8;
          case '4-6':
            return hasRatings && averageRating >= 4 && averageRating < 6;
          case 'unrated':
            return !hasRatings;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [familyActivities, filters, currentUser]);

  // Event handlers
  const handleSuggestVote = async (activity) => {
    try {
      const voteActivity = {
        ...activity,
        id: undefined, // Create new activity for voting
        ratings: {}, // Clear existing ratings for new vote
        confirmed_for_calendar: false,
        created_at: new Date().toISOString()
      };
      await addFamilyActivity(voteActivity);
    } catch (error) {
      console.error('Error suggesting activity for vote:', error);
    }
  };

  const handleUnarchive = async (activity) => {
    if (!window.confirm('Are you sure you want to restore this activity to the active list?')) {
      return;
    }
    
    try {
      await unarchiveFamilyActivity(activity.id);
    } catch (error) {
      console.error('Error unarchiving activity:', error);
      alert('Failed to restore activity. Please try again.');
    }
  };

  const handleScheduleActivity = async (activity, date, time) => {
    try {
      await scheduleToCalendar(activity, date, time);
      setShowScheduleModal(false);
      setActivityToSchedule(null);
    } catch (error) {
      console.error('Error scheduling activity:', error);
    }
  };

  const handleActivitySelect = (activity, originElement) => {
    setSelectedActivity(activity);
    setActivityDetailOrigin(originElement);
    setShowActivityDetail(true);
  };
  
  // Handle vote card click to show details
  const handleVoteCardClick = (activity, originElement) => {
    setSelectedActivity(activity);
    setActivityDetailOrigin(originElement);
    setShowActivityDetail(true);
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setShowActivityDetail(false);
    setShowAddForm(true);
  };

  // If tab=trips, render TripBrowseGrid component
  if (currentTab === 'trips') {
    return (
      <div className="activity-browse-page">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
          <div className="px-2 sm:px-2 py-2">
            {/* Top row: Back button, Section name with color bar, Add button */}
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-3">
                  {/* Color bar */}
                  <div className="w-2 h-6 bg-azure-500 rounded-full"></div>
                  <div className="min-w-0 flex-1">
                    <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight truncate">
                      Family Trips
                    </h1>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom row: Tab Navigation */}
          <div className="px-0">
            <div className="flex">
              <Link 
                to="/family" 
                className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
                style={{ minHeight: '48px' }}
              >
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <Lightbulb className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                  <span className="truncate">Things to Do</span>
                </div>
              </Link>
              <Link 
                to="/calendar" 
                className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
                style={{ minHeight: '48px' }}
              >
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <Calendar className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                  <span className="truncate">Calendar</span>
                </div>
              </Link>
              <Link 
                to="/family?tab=trips" 
                className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-azure-500 text-slate-900 bg-azure-50"
                style={{ minHeight: '48px' }}
              >
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <Plane className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                  <span className="truncate">Trips</span>
                </div>
              </Link>
              <Link 
                to="/family?tab=invites" 
                className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
                style={{ minHeight: '48px' }}
              >
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <Heart className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                  <span className="truncate">1:1</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Render TripBrowseGrid component */}
        <div className="px-2 sm:px-2 py-2">
          <TripBrowseGrid 
            trips={trips}
            onTripSelect={(trip, originElement) => {
              setSelectedTrip(trip);
              setTripDetailOrigin(originElement);
              setShowTripDetail(true);
            }}
            onAddTrip={() => {
              setEditingTrip(null);
              setShowTripForm(true);
            }}
          />
        </div>
        
        {/* Trip Detail Panel */}
        {showTripDetail && selectedTrip && (
          <TripDetailPanel
            trip={selectedTrip}
            isOpen={showTripDetail}
            onClose={() => {
              setShowTripDetail(false);
              setSelectedTrip(null);
              setTripDetailOrigin(null);
            }}
            onEdit={(trip) => {
              setEditingTrip(trip);
              setShowTripForm(true);
              setShowTripDetail(false);
            }}
            originElement={tripDetailOrigin}
          />
        )}
        
        {/* Trip Form Modal */}
        {showTripForm && (
          <TripForm
            trip={editingTrip}
            onClose={() => {
              setShowTripForm(false);
              setEditingTrip(null);
            }}
            mode={editingTrip ? 'edit' : 'add'}
          />
        )}
      </div>
    );
  }
  
  // If tab=invites, render FamilyInvitations component
  if (currentTab === 'invites') {
    return (
      <div className="activity-browse-page">
        {/* Header */}
        <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
          <div className="px-2 sm:px-2 py-2">
            {/* Top row: Back button, Section name with color bar, Add button */}
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-3">
                  {/* Color bar */}
                  <div className="w-2 h-6 bg-gold-500 rounded-full"></div>
                  <div className="min-w-0 flex-1">
                    <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight truncate">
                      1:1 Activities
                    </h1>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom row: Tab Navigation */}
          <div className="px-0">
            <div className="flex">
              <Link 
                to="/family" 
                className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
                style={{ minHeight: '48px' }}
              >
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <Lightbulb className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                  <span className="truncate">Things to Do</span>
                </div>
              </Link>
              <Link 
                to="/calendar" 
                className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
                style={{ minHeight: '48px' }}
              >
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <Calendar className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                  <span className="truncate">Calendar</span>
                </div>
              </Link>
              <Link 
                to="/family?tab=invites" 
                className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-gold-500 text-slate-900 bg-gold-50"
                style={{ minHeight: '48px' }}
              >
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <Heart className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                  <span className="truncate">1:1</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Render FamilyInvitations component */}
        <div className="px-2 sm:px-2 py-2">
          <FamilyInvitations />
        </div>
      </div>
    );
  }
  
  // Default: render the activity browse page
  return (
    <div className="activity-browse-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="px-2 sm:px-2 py-2">
          {/* Top row: Back button, Section name with color bar, Add button */}
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                style={{ minWidth: '44px', minHeight: '44px' }}
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3">
                {/* Color bar */}
                <div className="w-2 h-6 bg-gold-500 rounded-full"></div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight truncate">
                    {showArchived ? 'Archived Activities' : 'Things to Do'}
                  </h1>
                </div>
              </div>
            </div>

            {/* Archive Toggle - Responsive */}
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1 sm:gap-2 flex-shrink-0 ${
                showArchived 
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' 
                  : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              }`}
            >
              {showArchived ? (
                <>
                  <RotateCcw className="w-3 sm:w-4 h-3 sm:h-4" />
                  <span className="hidden sm:inline">Show Active</span>
                  <span className="sm:hidden">Active</span>
                </>
              ) : (
                <>
                  <Archive className="w-3 sm:w-4 h-3 sm:h-4" />
                  <span className="hidden sm:inline">View Archived</span>
                  <span className="sm:hidden">Archive</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Bottom row: Tab Navigation */}
        <div className="px-0">
          <div className="flex">
            <Link 
              to="/family" 
              className={`flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 ${
                currentTab === 'activities' ? 'border-gold-500 text-slate-900 bg-gold-50' : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
              }`}
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <Lightbulb className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                <span className="truncate">Things to Do</span>
                {(processedData.activeVotes.length > 0 || processedData.pendingRSVPs.length > 0) && (
                  <div className="bg-coral-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium flex-shrink-0">
                    {processedData.activeVotes.length + processedData.pendingRSVPs.length}
                  </div>
                )}
              </div>
            </Link>
            <Link 
              to="/calendar" 
              className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <Calendar className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                <span className="truncate">Calendar</span>
              </div>
            </Link>
            <Link 
              to="/family?tab=trips" 
              className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <Plane className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                <span className="truncate">Trips</span>
              </div>
            </Link>
            <Link 
              to="/family?tab=invites" 
              className="flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50"
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <Heart className="w-3 sm:w-4 h-3 sm:h-4 flex-shrink-0" />
                <span className="truncate">1:1</span>
              </div>
            </Link>
          </div>
        </div>
      </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6">

        <ActivityBrowseGrid
          activities={browseActivities}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          onActivitySelect={handleActivitySelect}
          onSuggestVote={handleSuggestVote}
          onScheduleActivity={(activity) => {
            setActivityToSchedule(activity);
            setShowScheduleModal(true);
          }}
          onUnarchive={showArchived ? handleUnarchive : undefined}
          showArchived={showArchived}
          onVoteCardClick={handleVoteCardClick}
        />
      </div>

      {/* Modals */}
      {showAddForm && (
        <EventForm
          event={editingActivity}
          onClose={() => {
            setShowAddForm(false);
            setEditingActivity(null);
          }}
          mode={editingActivity ? 'edit' : 'add'}
        />
      )}

      {showActivityDetail && selectedActivity && (
        <ActivityDetailPanel
          activity={selectedActivity}
          isOpen={showActivityDetail}
          onClose={() => {
            setShowActivityDetail(false);
            setSelectedActivity(null);
            setActivityDetailOrigin(null);
          }}
          onEdit={handleEditActivity}
          onSuggestVote={handleSuggestVote}
          onSchedule={(activity) => {
            setActivityToSchedule(activity);
            setShowScheduleModal(true);
            setShowActivityDetail(false);
          }}
          originElement={activityDetailOrigin}
        />
      )}

      {showScheduleModal && activityToSchedule && (
        <ScheduleActivityModal
          activity={activityToSchedule}
          onSchedule={handleScheduleActivity}
          onCancel={() => {
            setShowScheduleModal(false);
            setActivityToSchedule(null);
          }}
        />
      )}

      {/* Mobile Filters */}
      <MobileActivityFilters
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={(newFilters) => {
          setFilters(newFilters);
        }}
        initialFilters={filters}
      />
    </div>
  );
}